/** Deferred outputs collected from Rule Elements, keyed by selector (Foundry: rules/synthetics.ts). */
import type { SenseData } from "./actor-data";
import type { DamageAlteration, DamageDice } from "./damage-dice";
import type { EngineItem } from "./items";
import { cloneModifier, resolveModifiers, type Modifier, type ModifierAdjustment } from "./modifiers";
import { testPredicate, type RawPredicate } from "./predicate";
import type { RuleInstance } from "./rule-elements/base";
import type { EngineState } from "./state";
import type { DerivedStatistic } from "./statistics";
import type { DegreeOfSuccessString, MovementType } from "./values";
import type { EngineWeapon, WeaponRunes } from "./weapons";

export interface DeferredValueParams {
  /** Roll options to test the produced modifier's predicate against. */
  test?: Set<string>;
  /** Extra roll data (`@weapon`, `@spell`, ...) for formula resolution. */
  resolvables?: Record<string, unknown>;
  /** Extra `{weapon|...}` placeholder sources. */
  injectables?: Record<string, unknown>;
}

export type DeferredModifier = (params?: DeferredValueParams) => Modifier | null;

export interface DeferredDamageDiceOptions extends DeferredValueParams {
  selectors: string[];
  test: Set<string>;
}
export type DeferredDamageDice = (options: DeferredDamageDiceOptions) => DamageDice | null;

/** A Sense Rule Element's offer (Foundry: SenseSynthetic), merged into the senses when Perception is prepared. */
export interface SenseSynthetic {
  sense: SenseData;
  predicate: RawPredicate;
  force: boolean;
}

/** What a BaseSpeed Rule Element resolved to for one movement type (Foundry: BaseSpeedSynthetic). */
export interface BaseSpeedSynthetic {
  type: MovementType;
  value: number;
  source: string | null;
  force: boolean;
  /** The raw value, kept to classify a speed derived from another (equal, scaled, independent). */
  formula: string | number;
  dependsOn: MovementType[];
}

export interface MovementTypeSynthetic {
  dependsOn: MovementType[];
  deferred: (params?: { test?: Set<string> }) => BaseSpeedSynthetic | null;
  /** Whether the Rule Element's predicate passes, without resolving the value. */
  test: (params?: { test?: Set<string> }) => boolean;
}

/** A Note Rule Element's text for the rolls of a selector (Foundry: RollNotePF2e). */
export interface RollNote {
  selector: string;
  title: string | null;
  text: string;
  predicate: RawPredicate;
  parentOptions: string[];
  /** Degrees of success the note applies to; empty means all. */
  outcome: DegreeOfSuccessString[];
  visibility: "gm" | "owner" | null;
  source: string | null;
  sourceName: string | null;
}

/** A SubstituteRoll Rule Element's fixed die result (Foundry: RollSubstitution). */
export interface RollSubstitution {
  slug: string;
  label: string;
  predicate: RawPredicate;
  parentOptions: string[];
  value: number;
  required: boolean;
  selected: boolean;
  effectType: "fortune" | "misfortune";
  source: string | null;
  sourceName: string | null;
  /** Foundry's `removeAfterRoll`: the Effect ends after a Roll (always, only when the substitution was used, or when the predicate passes). */
  removeAfterRoll: boolean | "if-enabled" | RawPredicate;
}

export interface RollTwiceSynthetic {
  keep: "higher" | "lower";
  predicate: RawPredicate;
  parentOptions: string[];
  source: string | null;
  sourceName: string | null;
  /** Foundry's `removeAfterRoll`: the Effect ends after a Roll. */
  removeAfterRoll: boolean;
}

export interface MAPSynthetic {
  label: string;
  penalty: number;
  predicate: RawPredicate;
  parentOptions: string[];
  source: string | null;
}

/** Foundry's DEGREE_ADJUSTMENT_AMOUNTS: a shift, or a fixed outcome. */
export type DegreeAdjustmentAmount = -2 | -1 | 1 | 2 | DegreeOfSuccessString;

export interface DegreeAdjustment {
  label: string;
  amount: DegreeAdjustmentAmount;
}

export interface DegreeOfSuccessAdjustmentSynthetic {
  adjustments: Partial<Record<"all" | DegreeOfSuccessString, DegreeAdjustment>>;
  predicate: RawPredicate;
  parentOptions: string[];
  source: string | null;
  sourceName: string | null;
}

export interface DexterityModifierCapData {
  value: number;
  source: string;
}

export interface StrikingSynthetic {
  label: string;
  bonus: number;
  predicate: RawPredicate;
}

export interface PotencySynthetic {
  label: string;
  bonus: number;
  type: "item" | "potency";
  predicate: RawPredicate;
  property?: string[];
}

/** An AdjustStrike Rule Element's deferred change to matching strikes (Foundry: StrikeAdjustment). */
export interface StrikeAdjustment {
  adjustWeapon?: (weapon: EngineWeapon) => void;
  adjustTraits?: (weapon: EngineWeapon, traits: string[]) => void;
  adjustDamageRoll?: (weapon: EngineWeapon, args: { materials: Set<string> }) => void;
  source: string | null;
  sourceName: string | null;
}

export type CritSpecEffect = (DamageDice | Modifier | RollNote)[];
export type CritSpecSynthetic = (weapon: EngineWeapon, options: Set<string>) => CritSpecEffect | null;

/** A Strike Rule Element's weapon constructor; unarmed strikes take the handwraps' runes. */
export type DeferredStrike = (unarmedRunes?: WeaponRunes) => EngineWeapon | null;

/** An EphemeralEffect Rule Element's offer: an Effect or Condition applied for one roll (Foundry: DeferredEphemeralEffect). */
export interface EphemeralEffectSynthetic {
  uuid: string;
  affects: "target" | "origin";
  predicate: RawPredicate;
  parentOptions: string[];
  adjustName: boolean;
  source: string | null;
  sourceName: string | null;
  /** The effect's name when its Entry is at hand. */
  name: string | null;
  test: (options: Set<string>) => boolean;
}

export interface AuraEffectData {
  uuid: string;
  name: string | null;
  affects: "allies" | "enemies" | "all";
  events: ("enter" | "turn-start" | "turn-end")[];
  save: { type: string; dc: number | string } | null;
  predicate: RawPredicate;
  removeOnExit: boolean;
  includesSelf: boolean;
  /** Whether the engine applied this Effect to the bearer (includesSelf and the predicate passed). */
  appliedToSelf: boolean;
}

/** An Aura Rule Element's aura (Foundry: AuraData), without its canvas appearance. */
export interface AuraData {
  slug: string;
  radius: number;
  level: number | null;
  traits: string[];
  effects: AuraEffectData[];
  source: string | null;
  sourceName: string | null;
}

export interface TempHPSynthetic {
  value: number;
  /** Whether the temporary hit points apply when the Entry arrives, and whether they renew each turn. */
  events: { onCreate: boolean; onTurnStart: boolean };
  predicate: RawPredicate;
  enabled: boolean;
  source: string | null;
  sourceName: string | null;
}

export interface FastHealingSynthetic {
  type: "fast-healing" | "regeneration";
  /** A number or a roll formula ("1d6"). */
  value: number | string;
  details: string | null;
  deactivatedBy: string[];
  predicate: RawPredicate;
  enabled: boolean;
  source: string | null;
  sourceName: string | null;
}

export interface LoseHitPointsSynthetic {
  value: number;
  recoverable: boolean;
  source: string | null;
  sourceName: string | null;
}

export interface SpecialResourceData {
  slug: string;
  label: string;
  value: number;
  max: number;
  /** The physical Entry the resource is counted as, when it is one (versatile vials). */
  itemUuid: string | null;
  level: number | null;
  renew: "daily" | false;
  source: string | null;
  sourceName: string | null;
}

export interface Synthetics {
  modifiers: Record<string, DeferredModifier[]>;
  damageDice: Record<string, DeferredDamageDice[]>;
  modifierAdjustments: Record<string, ModifierAdjustment[]>;
  damageAlterations: Record<string, DamageAlteration[]>;
  degreeOfSuccessAdjustments: Record<string, DegreeOfSuccessAdjustmentSynthetic[]>;
  dexterityModifierCaps: DexterityModifierCapData[];
  rollNotes: Record<string, RollNote[]>;
  rollSubstitutions: Record<string, RollSubstitution[]>;
  rollTwice: Record<string, RollTwiceSynthetic[]>;
  multipleAttackPenalties: Record<string, MAPSynthetic[]>;
  strikes: Record<string, DeferredStrike>;
  strikeAdjustments: StrikeAdjustment[];
  striking: Record<string, StrikingSynthetic[]>;
  weaponPotency: Record<string, PotencySynthetic[]>;
  criticalSpecializations: { standard: CritSpecSynthetic[]; alternate: CritSpecSynthetic[] };
  ephemeralEffects: Record<string, { target: EphemeralEffectSynthetic[]; origin: EphemeralEffectSynthetic[] }>;
  /** ItemAlteration Rule Elements, applied to items that arrive later (synthetic strike weapons). */
  itemAlterations: RuleInstance[];
  /** Statistics SpecialStatistic Rule Elements built, by slug. */
  statistics: Map<string, DerivedStatistic>;
  senses: SenseSynthetic[];
  movementTypes: Partial<Record<MovementType, MovementTypeSynthetic[]>>;
  auras: Map<string, AuraData>;
  tempHP: TempHPSynthetic[];
  fastHealing: FastHealingSynthetic[];
  loseHitPoints: LoseHitPointsSynthetic[];
  resources: Record<string, SpecialResourceData>;
}

export function createSynthetics(): Synthetics {
  return {
    modifiers: { all: [], damage: [] },
    damageDice: { damage: [] },
    modifierAdjustments: { all: [], damage: [] },
    damageAlterations: {},
    degreeOfSuccessAdjustments: {},
    dexterityModifierCaps: [],
    rollNotes: {},
    rollSubstitutions: {},
    rollTwice: {},
    multipleAttackPenalties: {},
    strikes: {},
    strikeAdjustments: [],
    striking: {},
    weaponPotency: {},
    criticalSpecializations: { standard: [], alternate: [] },
    ephemeralEffects: {},
    itemAlterations: [],
    statistics: new Map(),
    senses: [],
    movementTypes: {},
    auras: new Map(),
    tempHP: [],
    fastHealing: [],
    loseHitPoints: [],
    resources: {},
  };
}

function unique<T>(values: T[]): T[] {
  return [...new Set(values)];
}

/** Every adjustment registered for the domains that names the slug (or no slug). */
export function extractModifierAdjustments(record: Record<string, ModifierAdjustment[]>, selectors: string[], slug: string): ModifierAdjustment[] {
  return unique(selectors.flatMap((s) => record[s] ?? [])).filter((a) => a.slug === slug || a.slug === null);
}

export function extractDamageAlterations(record: Record<string, DamageAlteration[]>, selectors: string[], slug: string): DamageAlteration[] {
  return unique(selectors.flatMap((s) => record[s] ?? [])).filter((a) => a.slug === slug || a.slug === null);
}

/** Every modifier registered for any of the domains, constructed now with the given params, adjustments attached. */
export function extractModifiers(synthetics: Synthetics, domains: string[], params: DeferredValueParams = {}): Modifier[] {
  const uniqueDomains = unique(domains);
  const modifiers: Modifier[] = [];
  const isDamage = uniqueDomains.some((d) => d.endsWith("damage"));
  for (const domain of uniqueDomains) {
    for (const deferred of synthetics.modifiers[domain] ?? []) {
      const modifier = deferred(params);
      if (modifier) {
        modifier.domains = [...uniqueDomains];
        modifier.adjustments = extractModifierAdjustments(synthetics.modifierAdjustments, uniqueDomains, modifier.slug);
        if (isDamage) modifier.alterations = extractDamageAlterations(synthetics.damageAlterations, uniqueDomains, modifier.slug);
        modifiers.push(modifier);
      }
    }
  }
  return modifiers;
}

/** Modifiers registered for the domains, constructed and tested with those domains' roll options. */
export function extractModifiersForDomains(state: EngineState, domains: string[]): Modifier[] {
  if (domains.length === 0) return [];
  return extractModifiers(state.synthetics, domains, { test: state.rollOptions.get(domains) });
}

export function extractDamageDice(synthetics: Synthetics, options: DeferredDamageDiceOptions): DamageDice[] {
  return unique(options.selectors).flatMap((s) => synthetics.damageDice[s] ?? []).flatMap((d) => d(options) ?? []);
}

export function extractNotes(record: Record<string, RollNote[]>, selectors: string[]): RollNote[] {
  return unique(selectors).flatMap((s) => (record[s] ?? []).map((n) => ({ ...n, predicate: structuredClone(n.predicate), outcome: [...n.outcome] })));
}

export function extractRollTwice(record: Record<string, RollTwiceSynthetic[]>, selectors: string[], options: Set<string>): "keep-higher" | "keep-lower" | false {
  const twices = unique(selectors).flatMap((s) => record[s] ?? []).filter((rt) => testPredicate(rt.predicate, new Set([...options, ...rt.parentOptions])));
  if (twices.length === 0) return false;
  if (twices.some((rt) => rt.keep === "higher") && twices.some((rt) => rt.keep === "lower")) return false;
  return twices[0]?.keep === "higher" ? "keep-higher" : "keep-lower";
}

export function extractRollSubstitutions(record: Record<string, RollSubstitution[]>, domains: string[], options: Set<string>): RollSubstitution[] {
  return unique(domains).flatMap((d) => (record[d] ?? []).map((s) => ({ ...s }))).filter((s) => testPredicate(s.predicate, new Set([...options, ...s.parentOptions])));
}

export function extractDegreeOfSuccessAdjustments(synthetics: Pick<Synthetics, "degreeOfSuccessAdjustments">, selectors: string[]): DegreeOfSuccessAdjustmentSynthetic[] {
  return unique(selectors).flatMap((s) => synthetics.degreeOfSuccessAdjustments[s] ?? []);
}

export function extractEphemeralEffects(synthetics: Pick<Synthetics, "ephemeralEffects">, domains: string[]): EphemeralEffectSynthetic[] {
  return unique(domains).flatMap((d) => {
    const record = synthetics.ephemeralEffects[d];
    return record ? [...record.origin, ...record.target] : [];
  });
}

/**
 * Foundry's processDamageCategoryStacking: stack the main and persistent modifiers separately,
 * and drop non-persistent parts when the base damage is entirely persistent.
 */
export function processDamageCategoryStacking(base: { category: string | null }[], options: { modifiers: Modifier[]; dice: DamageDice[]; test: Set<string> }): { modifiers: Modifier[]; dice: DamageDice[] } {
  const main = options.modifiers.filter((m) => m.damageCategory !== "persistent");
  const persistent = options.modifiers.filter((m) => m.damageCategory === "persistent");
  const modifiers = [...resolveModifiers(main, options.test).modifiers, ...resolveModifiers(persistent, options.test).modifiers];
  const allPersistent = base.length > 0 && base.every((b) => b.category === "persistent");
  return {
    modifiers: allPersistent ? modifiers.filter((m) => m.damageCategory === "persistent") : modifiers,
    dice: allPersistent ? options.dice.filter((d) => d.category === "persistent") : options.dice,
  };
}

/** Attach the adjustments registered for the domains to modifiers the engine built itself. */
export function withAdjustments(synthetics: Synthetics, modifiers: Modifier[], domains: string[]): Modifier[] {
  for (const modifier of modifiers) {
    if (modifier.adjustments.length === 0) modifier.adjustments = extractModifierAdjustments(synthetics.modifierAdjustments, domains, modifier.slug);
  }
  return modifiers;
}

export { cloneModifier };
